import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, isDeletionBlockedMessage } from "../contexts/AuthContext";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase/config";
import { offlineAuthService } from "../services/offlineAuthService";
import "../styles/auth.css";
import googleIcon from "../assets/images/google-icon.webp";
import { safeSessionSetJSON } from "../utils/localStorageUtils";
import { useThemeStore } from "../stores/themeStore";
import { getAuthErrorMessage } from "../utils/firebaseAuthErrors";
import Toast from "./Toast";

interface LoginProps {
  onToggleForm: () => void;
  onGoToRegister?: () => void;
}

// ─── Method Picker ────────────────────────────────────────────────────────────

interface MethodPickerProps {
  availableMethods: Array<"email" | "authenticator">;
  onSelect: (method: "email" | "authenticator") => void;
  onCancel: () => void;
  isDark: boolean;
}

const MethodPicker: React.FC<MethodPickerProps> = ({
  availableMethods,
  onSelect,
  onCancel,
  isDark,
}) => (
  <div className="grid grid-cols-1 gap-3 mt-4">
    <div className="flex items-center gap-3 mb-4">
      <button
        type="button"
        onClick={onCancel}
        className="bg-base-200 hover:bg-base-300 rounded-full p-2 transition-colors"
        aria-label="Back to login"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-base-content/80">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
      <div>
        <h2 className="text-2xl font-bold font-grotesk">Two-Factor Verification</h2>
        <p className="text-sm text-base-content/60">Choose how to verify your identity</p>
      </div>
    </div>

    <div className="space-y-3">
      {availableMethods.includes("email") && (
        <button
          onClick={() => onSelect("email")}
          className="w-full flex items-center gap-4 p-4 rounded-xl border border-base-300 bg-base-200 hover:bg-base-300 transition-all text-left"
        >
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
            </svg>
          </div>
          <div>
            <p className="font-medium text-sm text-base-content">Email Code</p>
            <p className="text-xs text-base-content/60 mt-0.5">Send a one-time code to your email</p>
          </div>
          <svg className="w-4 h-4 text-base-content/40 ml-auto" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6" />
          </svg>
        </button>
      )}

      {availableMethods.includes("authenticator") && (
        <button
          onClick={() => onSelect("authenticator")}
          className="w-full flex items-center gap-4 p-4 rounded-xl border border-base-300 bg-base-200 hover:bg-base-300 transition-all text-left"
        >
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
              <rect x="5" y="2" width="14" height="20" rx="2" />
              <line x1="12" y1="18" x2="12.01" y2="18" />
            </svg>
          </div>
          <div>
            <p className="font-medium text-sm text-base-content">Authenticator App</p>
            <p className="text-xs text-base-content/60 mt-0.5">Use Google Authenticator, Authy, or any TOTP app</p>
          </div>
          <svg className="w-4 h-4 text-base-content/40 ml-auto" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6" />
          </svg>
        </button>
      )}
    </div>

    <div className="text-center mt-2">
      <button
        type="button"
        onClick={onCancel}
        className="text-sm text-base-content/50 hover:text-base-content/80 transition-colors"
      >
        Cancel — back to Sign In
      </button>
    </div>
  </div>
);

// ─── 2FA Challenge ────────────────────────────────────────────────────────────

interface TwoFactorChallengeProps {
  uid: string;
  method: "email" | "authenticator";
  onSuccess: (code: string) => Promise<void>;
  onBack: () => void;
  onCancel: () => void;
  isDark: boolean;
  loading: boolean;
  error: string | null;
}

const TwoFactorChallenge: React.FC<TwoFactorChallengeProps> = ({
  uid,
  method,
  onSuccess,
  onBack,
  onCancel,
  isDark,
  loading,
  error,
}) => {
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [verifying, setVerifying] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const emailSentRef = useRef(false);

  const startCooldown = () => {
    setResendCooldown(60);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(cooldownRef.current!); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const sendEmailCode = async () => {
    setSendingCode(true);
    setSendError(null);
    try {
      const sendCode = httpsCallable(functions, "sendTwoFactorLoginCode");
      await sendCode({ uid });
      setCodeSent(true);
      startCooldown();
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (err: any) {
      setSendError(err?.message ?? "Failed to send code. Please try again.");
    } finally {
      setSendingCode(false);
    }
  };

  useEffect(() => {
    if (method === "email") {
      if (emailSentRef.current) return;
      emailSentRef.current = true;
      sendEmailCode();
    } else {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    return () => { if (cooldownRef.current) clearInterval(cooldownRef.current); };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) return;
    setVerifying(true);
    try {
      await onSuccess(trimmed);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-3 mt-4">
      <div className="flex items-center gap-3 mb-4">
        <button
          type="button"
          onClick={onBack}
          className="bg-base-200 hover:bg-base-300 rounded-full p-2 transition-colors"
          aria-label="Back"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-base-content/80">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <div>
          <h2 className="text-2xl font-bold font-grotesk">Two-Factor Verification</h2>
          <p className="text-sm text-base-content/60">
            {method === "email"
              ? "Check your email for a 6-digit code"
              : "Enter the code from your authenticator app"}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 p-4 bg-base-200 rounded-xl">
        {method === "email" ? (
          <>
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
              </svg>
            </div>
            <p className="text-sm text-base-content/70">
              {codeSent ? "A 6-digit code was sent to your email address." : sendingCode ? "Sending code to your email…" : "We'll send a 6-digit code to your email."}
            </p>
          </>
        ) : (
          <>
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                <rect x="5" y="2" width="14" height="20" rx="2" />
                <line x1="12" y1="18" x2="12.01" y2="18" />
              </svg>
            </div>
            <p className="text-sm text-base-content/70">
              Open your authenticator app and enter the 6-digit code for PEAK^.
            </p>
          </>
        )}
      </div>

      {(error || sendError) && (
        <div className="p-4 bg-red-500/15 text-red-700 dark:text-red-400 border border-red-500/30 rounded-xl text-sm">
          {sendError || error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-control">
          <div className="new-form-group w-full">
            <input
              ref={inputRef}
              className="form-element tracking-[0.4em] text-center text-xl font-mono"
              id="twofa-code"
              name="twofa-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              disabled={loading || sendingCode}
              placeholder="000000"
              maxLength={6}
            />
            <label htmlFor="twofa-code" className="text-base-content/80">
              Verification Code
            </label>
          </div>
        </div>

        <div className="mt-6">
          <button
            type="submit"
            disabled={verifying || code.length < 6 || (method === "email" && !codeSent)}
            className={`w-full text-center p-4 py-2.5 rounded-xl transition-colors ${isDark ? "bg-white text-black" : "bg-black text-white"} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <h3 className="flex justify-center items-center gap-2 font-semibold">
              {verifying ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-base-100"></div>
                  Verifying…
                </>
              ) : "Verify & Sign In"}
            </h3>
          </button>
        </div>
      </form>

      {method === "email" && (
        <div className="text-center mt-2">
          {resendCooldown > 0 ? (
            <p className="text-sm text-base-content/50">Resend code in {resendCooldown}s</p>
          ) : (
            <button
              type="button"
              onClick={sendEmailCode}
              disabled={sendingCode || loading}
              className="text-sm text-base-content/70 hover:text-base-content transition-colors disabled:opacity-50"
            >
              {sendingCode ? "Sending…" : "Resend code"}
            </button>
          )}
        </div>
      )}

      <div className="text-center mt-2">
        <button
          type="button"
          onClick={onCancel}
          className="text-sm text-base-content/50 hover:text-base-content/80 transition-colors"
        >
          Cancel — back to Sign In
        </button>
      </div>
    </div>
  );
};

// ─── Login ────────────────────────────────────────────────────────────────────

const Login: React.FC<LoginProps> = ({ onToggleForm, onGoToRegister }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const {
    login,
    continueWithGoogle,
    completeTwoFactorLogin,
    cancelTwoFactorLogin,
    selectTwoFactorMethod,
    pendingTwoFactor,
    authState,
  } = useAuth();
  const [localError, setLocalError] = useState<string | null>(null);
  const [twoFaError, setTwoFaError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ open: boolean; message: string }>({ open: false, message: "" });
  const navigate = useNavigate();
  const isDark = useThemeStore((s) => s.resolvedTheme) === "peakx-dark";

  const isOffline = !navigator.onLine;
  const hasCachedAuth = offlineAuthService.hasCachedAuth();

  useEffect(() => {
    if (!pendingTwoFactor) setTwoFaError(null);
  }, [pendingTwoFactor]);

  const showToast = (message: string) => {
    setToast({ open: true, message });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (!email || !password) { setLocalError("Please fill in all fields"); return; }
    if (isOffline) { setLocalError("Login requires an internet connection."); return; }
    try {
      await login(email, password);
    } catch (err) {
      showToast(getAuthErrorMessage(err));
    }
  };

  const handleGoogleSignIn = async () => {
    if (isOffline) { setLocalError("Google sign in requires an internet connection."); return; }
    setLocalError(null);
    try {
      const result: any = await continueWithGoogle();
      // result is undefined when 2FA is required (pendingTwoFactor is set instead)
      // New Google account with no Peak profile — continue registration in
      // the onboarding wizard (it resumes at the invite-code step).
      if (result?._tokenResponse?.isNewUser) {
        safeSessionSetJSON("googleNewUser", {
          email: result.user?.email ?? "",
          displayName: result.user?.displayName ?? "",
          uid: result.user?.uid ?? "",
        });
        if (onGoToRegister) {
          onGoToRegister();
        } else {
          navigate("/onboarding");
        }
      }
    } catch (err) {
      showToast(getAuthErrorMessage(err));
    }
  };

  const handleTwoFaSuccess = async (code: string) => {
    if (!pendingTwoFactor?.method) return;
    setTwoFaError(null);
    try {
      await completeTwoFactorLogin(code, pendingTwoFactor.method);
    } catch (err: any) {
      const errCode = err?.code ?? "";
      const errMessage = err?.message ?? "";
      if (errCode.includes("invalid-argument") || errMessage.includes("Invalid code")) {
        setTwoFaError("Incorrect code. Please try again.");
      } else if (errCode.includes("deadline-exceeded") || errMessage.includes("expired")) {
        setTwoFaError("Code expired. Please request a new one.");
      } else {
        setTwoFaError(errMessage || "Verification failed. Please try again.");
      }
    }
  };

  const handleTwoFaCancel = () => {
    setTwoFaError(null);
    cancelTwoFactorLogin();
  };

  // Back from challenge to picker (when multiple methods available)
  const handleBackToPicker = () => {
    if (!pendingTwoFactor) return;
    if (pendingTwoFactor.availableMethods.length > 1) {
      selectTwoFactorMethod(null as any);
    } else {
      handleTwoFaCancel();
    }
  };

  // Determine what to render in the 2FA area
  const showMethodPicker =
    pendingTwoFactor &&
    pendingTwoFactor.availableMethods.length > 1 &&
    !pendingTwoFactor.method;

  const showChallenge =
    pendingTwoFactor && pendingTwoFactor.method;

  return (
    <div className="min-h-screen bg-base-200">
      <div className="fixed top-0 left-0 right-0 bg-base-100 border-b border-base-200 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/landing" className="text-3xl font-groteskLogo font-bold hover:opacity-80 transition-opacity duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary rounded">
            <h1 className="text-3xl font-groteskLogo">
              PEAK<span className="font-bold text-primary -ml-1">^</span>
            </h1>
          </Link>
          <div className="text-sm text-base-content/80">
            <strong>{pendingTwoFactor ? "Verification" : "Login"}</strong>
          </div>
        </div>
      </div>

      <div className="mx-auto px-8 max-w-xl pt-24 py-16">
        <div className="p-0">
          {/* ── Method picker (multiple methods configured) ── */}
          {showMethodPicker && (
            <MethodPicker
              availableMethods={pendingTwoFactor!.availableMethods}
              onSelect={(m) => selectTwoFactorMethod(m)}
              onCancel={handleTwoFaCancel}
              isDark={isDark}
            />
          )}

          {/* ── 2FA Challenge ── */}
          {showChallenge && (
            <TwoFactorChallenge
              uid={pendingTwoFactor!.uid}
              method={pendingTwoFactor!.method!}
              onSuccess={handleTwoFaSuccess}
              onBack={handleBackToPicker}
              onCancel={handleTwoFaCancel}
              isDark={isDark}
              loading={authState.loading}
              error={twoFaError}
            />
          )}

          {/* ── Normal Login Form ── */}
          {!pendingTwoFactor && (
            <div className="grid grid-cols-1 gap-3 mt-4">
              <button
                type="button"
                onClick={() => (window.location.href = "/")}
                className="bg-base-200 hover:bg-base-300 rounded-full p-2 transition-colors w-fit"
                aria-label="Back to home"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-base-content/80">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>

              <div className="space-y-1 mb-4">
                <h2 className="text-2xl font-bold font-grotesk">Sign In</h2>
                <p className="text-sm text-base-content/80">Welcome back</p>
              </div>

              {isOffline && (
                <div className="p-4 mb-4 bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border border-yellow-500/30 rounded-xl">
                  You are currently offline.
                  {hasCachedAuth ? " Your previous session should be available." : " You need an internet connection to sign in."}
                </div>
              )}

              {localError && (
                <div className="p-4 mb-4 bg-red-500/15 text-red-700 dark:text-red-400 border border-red-500/30 rounded-xl">
                  {localError}
                </div>
              )}

              {/* Deletion-block message comes from AuthContext (set by the
                  onAuthStateChanged listener), not component-local state, so
                  it survives even if this component instance unmounts. */}
              {isDeletionBlockedMessage(authState.error) && (
                <div className="p-4 mb-4 bg-red-500/15 text-red-700 dark:text-red-400 border border-red-500/30 rounded-xl">
                  {authState.error}
                </div>
              )}

              <div className="form-control">
                <div className="new-form-group w-full">
                  <input
                    className="form-element"
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={authState.loading || isOffline}
                    placeholder="Enter your email"
                  />
                  <label htmlFor="email" className="text-base-content/80">Email Address</label>
                </div>
              </div>

              <div className="form-control">
                <div className="new-form-group w-full">
                  <input
                    className="form-element"
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={authState.loading || isOffline}
                    placeholder="Enter your password"
                  />
                  <label htmlFor="password" className="text-base-content/80">Password</label>
                </div>
              </div>

              <div className="flex justify-end -mt-2">
                <a href="/forgot-password" className="text-base-content hover:text-base-content/80 text-sm">
                  Forgot password?
                </a>
              </div>

              <div className="mt-10">
                <button
                  type="submit"
                  onClick={handleSubmit}
                  className={`w-full text-center p-4 py-2.5 rounded-xl transition-colors ${isDark ? "bg-white text-black" : "bg-black text-white"}`}
                  disabled={authState.loading || isOffline}
                >
                  <h3 className="flex justify-center items-center gap-2 font-semibold">
                    {authState.loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-base-100"></div>
                        Signing In…
                      </>
                    ) : "Sign In"}
                  </h3>
                </button>
              </div>

              <div className="flex items-center gap-2 justify-center text-sm text-base-content/80 mt-4">
                <div className="w-full border-t border-base-200"></div>
                <p>Or</p>
                <div className="w-full border-t border-base-200"></div>
              </div>

              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={authState.loading || isOffline}
                className="py-3 flex items-center gap-2 justify-center bg-base-100 border border-base-300 w-full mt-2 text-base-content/80 hover:bg-base-200 transition-colors rounded-xl"
              >
                <img src={googleIcon} alt="Google Icon" className="w-5 h-5" />
                Continue with Google
              </button>

              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={onToggleForm}
                  className="text-base-content/80 hover:text-base-content text-sm transition-colors"
                  disabled={authState.loading || isOffline}
                >
                  I don't have an account. Sign Up
                </button>
              </div>

              <div className="text-center mt-6">
                <a
                  href="https://help.peak.app/en/articles/14471378-password-reset-troubleshooting"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-base-content/50 hover:text-base-content/80 text-sm transition-colors"
                >
                  Need Help?
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      <Toast
        open={toast.open}
        type="error"
        message={toast.message}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
};

export default Login;